import { useState, useEffect } from "react";
import { Zap, Stamp, Trophy, Sun, TrendingDown, ChevronRight } from "lucide-react";
import { API, axios } from "@/api";

const SIGNAL_ICONS = {
  punch_card_nearly_complete: { icon: Stamp, label: "Punch card", color: "text-amber-600 bg-amber-50" },
  proven_vendor: { icon: Trophy, label: "Proven vendor", color: "text-yellow-600 bg-yellow-50" },
  clear_weather: { icon: Sun, label: "Clear weather", color: "text-sky-600 bg-sky-50" },
  low_competition_day: { icon: TrendingDown, label: "Low competition", color: "text-green-600 bg-green-50" },
  high_confidence_listing: { icon: Trophy, label: "Trusted listing", color: "text-purple-600 bg-purple-50" },
  consistency_bonus: { icon: Trophy, label: "Consistent", color: "text-emerald-600 bg-emerald-50" },
  holiday_campaign_active: { icon: null, label: "Holiday", color: "text-orange-600 bg-orange-50", isEmoji: true },
};

export default function PowerDealsSection({ user, navigate }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.birthday) { setLoading(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/power-deals`);
        setDeals(res.data.power_deals || []);
      } catch { /* graceful */ }
      finally { setLoading(false); }
    })();
  }, [user?.birthday]);

  if (!user?.birthday) return null;
  if (!loading && deals.length === 0) return null;

  return (
    <section className="mt-4 px-4" data-testid="power-deals-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-base font-bold text-slate-800 flex items-center gap-1.5">
            <Zap className="w-4 h-4 text-amber-500" /> Power Deals
          </h2>
          <p className="text-[10px] text-slate-400 mt-0.5">Your birthday, maximized</p>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
        {loading ? (
          [0,1,2].map(i => (
            <div key={i} className="min-w-[240px] h-36 bg-slate-100 rounded-xl animate-pulse shrink-0" data-testid={`power-deal-skeleton-${i}`} />
          ))
        ) : (
          deals.map((deal) => (
            <div
              key={deal.business_id}
              onClick={() => navigate(`/offer/${deal.business_id}`)}
              className="min-w-[240px] bg-white rounded-xl border border-amber-100 shadow-sm p-3 shrink-0 cursor-pointer hover:shadow-md transition group"
              data-testid={`power-deal-card-${deal.business_id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{deal.business_name}</p>
                  <p className="text-xs text-slate-400 truncate">{deal.offer_description}</p>
                </div>
                <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 rounded text-[10px] font-bold flex items-center gap-0.5 shrink-0" data-testid="power-deal-badge">
                  <Zap className="w-3 h-3" /> Power
                </span>
              </div>

              {/* Signal pills */}
              <div className="flex flex-wrap gap-1 mt-2">
                {(deal.signals_matched || []).filter(s => s !== "offer_exists").map((signal) => {
                  const cfg = SIGNAL_ICONS[signal];
                  if (!cfg) return null;
                  const Icon = cfg.icon;
                  const emoji = (signal === "holiday_campaign_active" && deal.signal_details?.holiday_emoji) ? deal.signal_details.holiday_emoji : null;
                  return (
                    <span key={signal} className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium flex items-center gap-0.5 ${cfg.color}`} data-testid={`signal-${signal}`}>
                      {emoji ? <span className="text-xs">{emoji}</span> : Icon ? <Icon className="w-2.5 h-2.5" /> : null} {cfg.label}
                    </span>
                  );
                })}
              </div>

              <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-50">
                <span className="text-[10px] text-slate-400">{deal.listing_type === "food_truck" ? "Food Truck" : deal.category}</span>
                <ChevronRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-amber-500 transition" />
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}
